import React from 'react';
import { Link } from 'react-router-dom';
import SEOHelmet from '../components/SEOHelmet';
import SkipToContent from '../components/SkipToContent';
import OtherStates from '../components/OtherStates';

/**
 * New York Professional License Renewal Landing Page
 * 
 * Comprehensive guide for NY licensed professionals including:
 * - 11 professions with CE requirements
 * - State board resources and contact information
 * - Renewal deadlines and cycle information
 * - Category breakdowns for each profession
 */

interface ProfessionInfo {
  id: string;
  name: string;
  icon: string;
  credits: number;
  cycleYears: number;
  terminology: string;
  highlights: string[];
  stateBoard: string;
  boardUrl: string;
}

const NYLicenseRenewal: React.FC = () => {
  const professions: ProfessionInfo[] = [
    {
      id: 'ARCH',
      name: 'Architect',
      icon: '🏛️',
      credits: 36,
      cycleYears: 3,
      terminology: 'LU',
      highlights: ['12 LU minimum in Health, Safety & Welfare', '1 LU in Accessibility', '2 LU in Sustainable Design'],
      stateBoard: 'NY State Education Department',
      boardUrl: 'http://www.op.nysed.gov/prof/arch/'
    },
    {
      id: 'PE',
      name: 'Professional Engineer',
      icon: '⚙️',
      credits: 36,
      cycleYears: 3,
      terminology: 'PDH',
      highlights: ['36 PDH every 3 years', 'Minimum 1 PDH in Ethics', 'General engineering topics accepted'],
      stateBoard: 'NY State Education Department',
      boardUrl: 'http://www.op.nysed.gov/prof/pels/'
    },
    {
      id: 'RE',
      name: 'Real Estate Agent/Broker',
      icon: '🏠',
      credits: 22.5,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['22.5 hours biennial', '1 hr Fair Housing', '1 hr Agency & Fiduciary Duties', '1 hr Recent Legal Matters'],
      stateBoard: 'NY Department of State',
      boardUrl: 'https://www.dos.ny.gov/licensing/real-estate/'
    },
    {
      id: 'APPR',
      name: 'Real Estate Appraiser',
      icon: '📊',
      credits: 28,
      cycleYears: 2,
      terminology: 'hours',
      highlights: ['28 hours biennial', '7-hour National USPAP Update (mandatory)', '4-7 hours Fair Housing & Fair Lending'],
      stateBoard: 'NY Department of State',
      boardUrl: 'https://www.dos.ny.gov/licensing/real-estate/continuing-education.html'
    },
    {
      id: 'CPA',
      name: 'Certified Public Accountant',
      icon: '💼',
      credits: 40,
      cycleYears: 1,
      terminology: 'CPE',
      highlights: [
        '40 CPE hrs/year in any recognized subject area — OR — 24 hrs/year concentrated in one subject area',
        'Ethics: 4 hrs in the 3 calendar years before license expiration (counts toward 40-hr requirement; counts toward 24-hr only if taken in the concentration subject)',
        'Attest/compilation supervisors must also complete 40 CPE in audit, accounting, and/or attest in the 3 years prior to performing such services',
        'Instructor & published article/book credit capped at 50% of total required hours',
        'Self-study must be from a NASBA-registered or NY Board-registered provider',
        '6 named subject areas: Accounting, Auditing, Attest, Taxation, Advisory Services, Specialized Knowledge',
      ],
      stateBoard: 'NY State Education Department',
      boardUrl: 'https://www.op.nysed.gov/certified-public-accountants'
    },
    {
      id: 'LCSW',
      name: 'Licensed Clinical Social Worker',
      icon: '🧠',
      credits: 36,
      cycleYears: 3,
      terminology: 'hours',
      highlights: ['36 hours triennial', '3 hours Appropriate Professional Boundaries', '2 hours Child Abuse (one-time)', 'Max 12 hours self-study'],
      stateBoard: 'NY State Education Department',
      boardUrl: 'http://www.op.nysed.gov/prof/sw/'
    },
    {
      id: 'LMSW',
      name: 'Licensed Master Social Worker',
      icon: '🤝',
      credits: 36,
      cycleYears: 3,
      terminology: 'hours',
      highlights: ['36 hours triennial', '3 hours Appropriate Professional Boundaries', '2 hours Child Abuse (one-time)', 'Max 12 hours self-study'],
      stateBoard: 'NY State Education Department',
      boardUrl: 'http://www.op.nysed.gov/prof/sw/'
    },
    {
      id: 'RN',
      name: 'Registered Nurse',
      icon: '⚕️',
      credits: 0,
      cycleYears: 3,
      terminology: 'training',
      highlights: ['Infection Control every 4 years (7 core elements)', 'Child Abuse 2 hours (one-time, updated by Nov 17, 2026)', 'Triennial registration'],
      stateBoard: 'NY State Education Department',
      boardUrl: 'http://www.op.nysed.gov/prof/nurse/'
    },
    {
      id: 'LPN',
      name: 'Licensed Practical Nurse',
      icon: '💉',
      credits: 0,
      cycleYears: 3,
      terminology: 'training',
      highlights: ['Infection Control every 4 years (7 core elements)', 'Child Abuse 2 hours (one-time, updated by Nov 17, 2026)', 'Triennial registration'],
      stateBoard: 'NY State Education Department',
      boardUrl: 'http://www.op.nysed.gov/prof/nurse/'
    },
    {
      id: 'APRN',
      name: 'Advanced Practice Registered Nurse',
      icon: '🩺',
      credits: 3,
      cycleYears: 3,
      terminology: 'hours',
      highlights: ['3 hours Pain Management/Palliative Care/Addiction', 'Infection Control every 4 years', 'Child Abuse 2 hours (one-time)', 'For prescribers'],
      stateBoard: 'NY State Education Department',
      boardUrl: 'http://www.op.nysed.gov/prof/nurse/'
    },
    {
      id: 'LAW',
      name: 'Attorney',
      icon: '⚖️',
      credits: 24,
      cycleYears: 2,
      terminology: 'CLE',
      highlights: ['24 CLE credits biennial', '4 hours Ethics & Professionalism', '1 hour Diversity, Inclusion & Elimination of Bias'],
      stateBoard: 'NY State CLE Board',
      boardUrl: 'http://www.nycourts.gov/attorneys/cle/'
    }
  ];

  const structuredData = [
    // Main WebPage schema
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "New York Professional License Renewal Requirements",
      "description": "Complete guide to New York professional license renewal requirements for Architects, Engineers, CPAs, Nurses, Social Workers, and more.",
      "url": "https://cectrack.com/ny-license-renewal",
      "about": {
        "@type": "Thing",
        "name": "Professional License Renewal",
        "sameAs": [
          "https://www.op.nysed.gov/",
          "https://www.dos.ny.gov/"
        ]
      },
      "publisher": {
        "@type": "Organization",
        "name": "CEC Track",
        "url": "https://cectrack.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://cectrack.com/og-image.png"
        }
      },
      "mainEntity": {
        "@type": "ItemList",
        "name": "New York Professional Licenses",
        "numberOfItems": 11,
        "itemListElement": professions.map((prof, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "item": {
            "@type": "Course",
            "name": prof.name,
            "description": `${prof.name} license renewal requires ${prof.credits} ${prof.terminology} every ${prof.cycleYears} year(s)`,
            "provider": {
              "@type": "Organization",
              "name": prof.stateBoard,
              "url": prof.boardUrl
            }
          }
        }))
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://cectrack.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "New York License Renewal",
            "item": "https://cectrack.com/ny-license-renewal"
          }
        ]
      }
    },
    // FAQPage schema
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How many CE hours do I need to renew my New York professional license?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "CE requirements vary by profession in New York. Architects and Engineers need 36 PDH/LU every 3 years, CPAs need 40 hours annually (24 if not providing attest services), Nurses need 3 contact hours per registration period, and Social Workers need 36 hours every 3 years."
          }
        },
        {
          "@type": "Question",
          "name": "Which state agency handles professional license renewal in New York?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Most New York professional licenses are overseen by the NY State Education Department (NYSED) Office of the Professions, including Architects, Engineers, CPAs, Nurses, and Social Workers. Real Estate professionals are licensed by the NY Department of State."
          }
        },
        {
          "@type": "Question",
          "name": "How often do I need to renew my New York professional license?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Renewal periods vary by profession. CPAs renew annually, Real Estate professionals renew every 2 years, and most other professions (Architects, Engineers, Social Workers) renew every 3 years. Nurses renew every 4 years."
          }
        },
        {
          "@type": "Question",
          "name": "Can I use online courses for New York CE requirements?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, most New York professions accept online continuing education courses from approved providers. However, some professions have limits on self-study or online hours. Check with your specific state board for approved providers and requirements."
          }
        }
      ]
    },
    // GovernmentOrganization schemas for state boards
    {
      "@context": "https://schema.org",
      "@type": "GovernmentOrganization",
      "name": "New York State Education Department - Office of the Professions",
      "url": "http://www.op.nysed.gov/",
      "areaServed": "New York",
      "description": "Licenses and regulates Architects, Engineers, CPAs, Nurses, Social Workers, and other professionals in New York State"
    },
    {
      "@context": "https://schema.org",
      "@type": "GovernmentOrganization",
      "name": "New York Department of State - Division of Licensing Services",
      "url": "https://www.dos.ny.gov/",
      "areaServed": "New York",
      "description": "Licenses and regulates Real Estate professionals and Appraisers in New York State"
    }
  ];

  return (
    <>
      <SkipToContent />
      <SEOHelmet
        title="New York Professional License Renewal | CE Requirements & Deadlines"
        description="Complete guide to New York professional license renewal requirements. Track CE credits for Architects, Engineers, CPAs, Nurses, Social Workers, and more."
        keywords="New York license renewal, NY CE requirements, NY continuing education, New York professional licenses, NYSED, NY DOS, New York nurses, NY CPAs, NY engineers"
        canonical="https://cectrack.com/ny-license-renewal"
        ogImage="https://cectrack.com/og-image.png"
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <section 
        aria-label="Page header"
        style={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          color: 'white',
          padding: '80px 0 60px',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            opacity: 0.4
          }}
        />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="row justify-content-center text-center">
            <div className="col-lg-10">
              <h1 style={{ fontSize: '3rem', fontWeight: '700', marginBottom: '1.5rem', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                New York Professional License Renewal
              </h1>
              <p style={{ fontSize: '1.3rem', marginBottom: '2rem', opacity: 0.95 }}>
                Never miss a deadline. Track your continuing education requirements across all your New York professional licenses in one place.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link 
                  to="/register" 
                  className="btn btn-light btn-lg px-5 py-3"
                  style={{ fontWeight: '600', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                >
                  Start Tracking Free
                </Link>
                <a 
                  href="#professions" 
                  className="btn btn-outline-light btn-lg px-5 py-3"
                  style={{ fontWeight: '600', borderRadius: '8px' }}
                >
                  View Requirements
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section aria-label="Statistics" style={{ padding: '60px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row g-4 text-center">
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }} aria-label="11 professions">11</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Professions Supported</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }} aria-label="Over 50 categories">50+</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Category Requirements</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }} aria-label="100 percent compliant">100%</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>NYSED Compliant</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="p-4">
                <div style={{ fontSize: '3rem', fontWeight: '700', color: '#1e3a8a', marginBottom: '0.5rem' }} aria-label="24/7 access">24/7</div>
                <div style={{ fontSize: '1.1rem', color: '#6c757d' }}>Access Anywhere</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professions Grid */}
      <main id="main-content">
        <section id="professions" aria-labelledby="professions-heading" style={{ padding: '80px 0' }}>
          <div className="container">
            <div className="text-center mb-5">
              <h2 id="professions-heading" style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
                New York CE Requirements by Profession
              </h2>
              <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
                Select your profession to see detailed continuing education requirements
              </p>
            </div>

          <div className="row g-4">
            {professions.map((prof) => (
              <div key={prof.id} className="col-md-6 col-lg-4">
                <div 
                  className="card h-100 border-0 shadow-sm"
                  style={{ 
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                  }}
                >
                  <div className="card-body p-4">
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{prof.icon}</div>
                    <h5 style={{ fontSize: '1.3rem', fontWeight: '600', marginBottom: '1rem' }}>
                      {prof.name}
                    </h5>
                    
                    <div 
                      style={{
                        backgroundColor: '#e7f3ff',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        marginBottom: '1rem'
                      }}
                    >
                      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e3a8a' }}>
                        {prof.credits > 0 ? `${prof.credits} ${prof.terminology}` : 'Mandated Training'}
                      </div>
                      <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                        {prof.cycleYears === 1 ? 'Annual' : prof.cycleYears === 2 ? 'Biennial' : 'Triennial'} Renewal
                      </div>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#6c757d', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
                        Key Requirements:
                      </div>
                      <ul style={{ fontSize: '0.9rem', margin: 0, paddingLeft: '1.2rem' }}>
                        {prof.highlights.map((highlight, idx) => (
                          <li key={idx} style={{ marginBottom: '0.3rem' }}>{highlight}</li>
                        ))}
                      </ul>
                    </div>

                    <div 
                      style={{
                        paddingTop: '1rem',
                        borderTop: '1px solid #e9ecef',
                        fontSize: '0.85rem',
                        color: '#6c757d'
                      }}
                    >
                      <strong>{prof.stateBoard}</strong>
                      <br />
                      <a 
                        href={prof.boardUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#3b82f6', textDecoration: 'none' }}
                        onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                        onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                      >
                        Visit State Board →
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose CEC Track */}
      <section style={{ padding: '80px 0', backgroundColor: '#f8f9fa' }}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
                Simplify Your New York License Management
              </h2>
              <p style={{ fontSize: '1.1rem', color: '#6c757d', marginBottom: '2rem' }}>
                Managing multiple New York professional licenses doesn't have to be complicated. 
                CEC Track automatically tracks your requirements, calculates your progress, and sends 
                timely reminders so you never miss a renewal deadline.
              </p>
              <ul style={{ fontSize: '1.1rem', listStyle: 'none', padding: 0 }}>
                {[
                  'Automated deadline tracking for all NY professions',
                  'Upload certificates and match to requirements',
                  'Real-time progress reports by category',
                  'Multi-state support for professionals licensed in multiple states',
                  'Secure cloud storage for all documents',
                  'Email notifications for upcoming deadlines'
                ].map((feature, idx) => (
                  <li key={idx} style={{ marginBottom: '1rem', paddingLeft: '2rem', position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: '#10b981', fontSize: '1.5rem' }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-lg-6">
              <div 
                className="p-5 text-center"
                style={{
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                }}
              >
                <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📱</div>
                <h3 style={{ fontSize: '1.8rem', fontWeight: '700', marginBottom: '1rem' }}>
                  Start Tracking Today
                </h3>
                <p style={{ color: '#6c757d', marginBottom: '2rem' }}>
                  Join hundreds of New York professionals who trust CEC Track to manage their continuing education.
                </p>
                <Link 
                  to="/register" 
                  className="btn btn-primary btn-lg px-5 py-3"
                  style={{ 
                    fontWeight: '600',
                    background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                    border: 'none',
                    borderRadius: '8px'
                  }}
                >
                  Create Free Account
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* State Resources */}
      <section aria-labelledby="resources-heading" style={{ padding: '80px 0' }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 id="resources-heading" style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1rem' }}>
              New York State Resources
            </h2>
            <p style={{ fontSize: '1.2rem', color: '#6c757d' }}>
              Official resources and contact information for New York professional licensing
            </p>
          </div>

          <div className="row g-4">
            <div className="col-md-6">
              <article className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                    NY State Education Department (NYSED)
                  </h3>
                  <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                    Regulates most professional licenses in New York including architects, engineers, 
                    social workers, nurses, and CPAs.
                  </p>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Phone:</strong> <a href="tel:+15184743817" aria-label="Call NYSED at 518-474-3817">(518) 474-3817</a>
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Website:</strong>{' '}
                    <a href="http://www.op.nysed.gov/" target="_blank" rel="noopener noreferrer" aria-label="Visit NYSED website">
                      www.op.nysed.gov
                    </a>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Office:</strong> 89 Washington Avenue, Albany, NY 12234
                  </div>
                  <a 
                    href="http://www.op.nysed.gov/prof/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary"
                    aria-label="View all NYSED professions"
                  >
                    View All NYSED Professions
                  </a>
                </div>
              </article>
            </div>

            <div className="col-md-6">
              <article className="card border-0 shadow-sm h-100">
                <div className="card-body p-4">
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1rem' }}>
                    NY Department of State (DOS)
                  </h3>
                  <p style={{ color: '#6c757d', marginBottom: '1rem' }}>
                    Regulates real estate licenses including brokers, salespersons, and appraisers in New York.
                  </p>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Phone:</strong> <a href="tel:+15184744429" aria-label="Call NY DOS at 518-474-4429">(518) 474-4429</a>
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>Website:</strong>{' '}
                    <a href="https://www.dos.ny.gov/" target="_blank" rel="noopener noreferrer" aria-label="Visit NY DOS website">
                      www.dos.ny.gov
                    </a>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <strong>Office:</strong> One Commerce Plaza, 99 Washington Avenue, Albany, NY 12231
                  </div>
                  <a 
                    href="https://www.dos.ny.gov/licensing/real-estate/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-outline-primary"
                    aria-label="Visit NY DOS Real Estate Division"
                  >
                    Real Estate Division
                  </a>
                </div>
              </article>
            </div>
          </div>
        </div>
      </section>

      {/* Other States Cross-Linking */}
      <OtherStates currentState="NY" />

      {/* CTA Section */}
      <section 
        aria-label="Call to action"
        style={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          color: 'white',
          padding: '80px 0'
        }}
      >
        <div className="container text-center">
          <h2 style={{ fontSize: '2.5rem', fontWeight: '700', marginBottom: '1.5rem' }}>
            Ready to Simplify Your New York License Renewal?
          </h2>
          <p style={{ fontSize: '1.25rem', marginBottom: '2rem', opacity: 0.95 }}>
            Join professionals across New York who never worry about missing CE requirements
          </p>
          <Link 
            to="/register" 
            className="btn btn-light btn-lg px-5 py-3"
            style={{ fontWeight: '600', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.2)' }}
          >
            Get Started Free
          </Link>
        </div>
      </section>
      </main>
    </>
  );
};

export default NYLicenseRenewal;
